import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box, Container, Heading, Text, Button, Flex, Icon, Badge, Spinner,
  Tabs, TabList, TabPanels, Tab, TabPanel,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  FaSignOutAlt, FaMapMarkerAlt, FaCalendarAlt, FaCalendarTimes, FaBriefcase, FaWallet,
} from 'react-icons/fa';
import { supabase, isSupabaseConfigured } from '../lib/supabaseClient';
import { formatDate } from '../lib/content';
import ItemManager from '../Components/admin/ItemManager';
import ApplicationsPanel from '../Components/admin/ApplicationsPanel';

const PromotionsPanel = () => {
  const { i18n } = useTranslation();

  return (
    <ItemManager
      table="promotions"
      folder="promotions"
      newLabelKey="admin.newPromotion"
      defaultValues={{ start_date: '', end_date: '' }}
      fields={[
        { name: 'start_date', labelKey: 'admin.startDate', type: 'date' },
        { name: 'end_date', labelKey: 'admin.endDate', type: 'date' },
      ]}
      renderBadges={(item) => (
        <>
          {item.start_date && (
            <Badge colorScheme="brand" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaCalendarAlt} mr={1} />
              {formatDate(item.start_date, i18n.language)}
            </Badge>
          )}
          {item.end_date && (
            <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaCalendarTimes} mr={1} />
              {formatDate(item.end_date, i18n.language)}
            </Badge>
          )}
        </>
      )}
    />
  );
};

const RealisationsPanel = () => {
  const { i18n } = useTranslation();

  return (
    <ItemManager
      table="realisations"
      folder="realisations"
      newLabelKey="admin.newRealisation"
      defaultValues={{ location: '' }}
      fields={[{ name: 'location', labelKey: 'admin.location', type: 'text' }]}
      renderBadges={(item) => (
        <>
          {item.location && (
            <Badge colorScheme="brand" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaMapMarkerAlt} mr={1} />
              {item.location}
            </Badge>
          )}
          {item.created_at && (
            <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaCalendarAlt} mr={1} />
              {formatDate(item.created_at, i18n.language)}
            </Badge>
          )}
        </>
      )}
    />
  );
};

const JobsPanel = () => {
  const { t, i18n } = useTranslation();

  return (
    <ItemManager
      table="jobs"
      folder="jobs"
      newLabelKey="admin.newJob"
      defaultValues={{ location: '', contract_type: '', salary: '', deadline: '' }}
      fields={[
        { name: 'location', labelKey: 'admin.location', type: 'text' },
        {
          name: 'contract_type',
          labelKey: 'admin.contractType',
          type: 'select',
          options: [
            { value: 'full_time', labelKey: 'admin.contractFullTime' },
            { value: 'part_time', labelKey: 'admin.contractPartTime' },
            { value: 'internship', labelKey: 'admin.contractInternship' },
            { value: 'temporary', labelKey: 'admin.contractTemporary' },
            { value: 'volunteer', labelKey: 'admin.contractVolunteer' },
          ],
        },
        { name: 'salary', labelKey: 'admin.salary', type: 'text' },
        { name: 'deadline', labelKey: 'admin.deadline', type: 'date' },
      ]}
      renderBadges={(item) => (
        <>
          {item.location && (
            <Badge colorScheme="brand" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaMapMarkerAlt} mr={1} />
              {item.location}
            </Badge>
          )}
          {item.contract_type && (
            <Badge colorScheme="purple" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaBriefcase} mr={1} />
              {t(`admin.contract${item.contract_type.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join('')}`)}
            </Badge>
          )}
          {item.salary && (
            <Badge colorScheme="green" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaWallet} mr={1} />
              {item.salary}
            </Badge>
          )}
          {item.deadline && (
            <Badge colorScheme="accent" color="brand.900" borderRadius="full" px={3} py={0.5} fontSize="xs">
              <Icon as={FaCalendarAlt} mr={1} />
              {formatDate(item.deadline, i18n.language)}
            </Badge>
          )}
        </>
      )}
    />
  );
};

const AdminDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setChecking(false);
      return undefined;
    }
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setChecking(false);
    });
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) {
      setIsAdmin(null);
      return undefined;
    }
    let cancelled = false;
    supabase.rpc('is_admin').then(({ data, error }) => {
      if (!cancelled && !error) setIsAdmin(data === true);
    });
    return () => {
      cancelled = true;
    };
  }, [session]);

  if (!isSupabaseConfigured) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="surface.muted" px={4}>
        <Box maxW="480px" textAlign="center" bg="surface.base" borderRadius="2xl" p={10} boxShadow="md">
          <Heading as="h1" size="lg" mb={4}>{t('admin.title')}</Heading>
          <Text color="text.soft">{t('admin.notConfigured')}</Text>
        </Box>
      </Flex>
    );
  }

  if (checking) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="surface.muted">
        <Spinner size="xl" color="brand.500" thickness="4px" />
      </Flex>
    );
  }

  if (!session) {
    return <Navigate to="/admin/login" replace />;
  }

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login', { replace: true });
  };

  return (
    <Box minH="100vh" bg="surface.muted">
      <Flex
        as="header"
        bg="brand.900"
        color="white"
        px={{ base: 4, md: 8 }}
        py={4}
        justify="space-between"
        align="center"
        flexWrap="wrap"
        gap={3}
      >
        <Heading size="md">
          COOPEC
          <Box as="span" color="accent.400">MICROTOUS</Box>
          <Box as="span" fontSize="sm" fontWeight={400} ml={3} color="whiteAlpha.700">
            — {t('admin.title')}
          </Box>
        </Heading>
        <Flex align="center" gap={4}>
          <Text fontSize="sm" color="whiteAlpha.800" display={{ base: 'none', md: 'block' }}>
            {session.user.email}
          </Text>
          <Button
            leftIcon={<FaSignOutAlt />}
            size="sm"
            colorScheme="accent"
            color="brand.900"
            _hover={{ bg: 'accent.300' }}
            onClick={signOut}
          >
            {t('admin.signOut')}
          </Button>
        </Flex>
      </Flex>

      {isAdmin === false && (
        <Box bg="orange.100" color="orange.900" px={{ base: 4, md: 8 }} py={3}>
          <Text fontSize="sm">{t('admin.notAdminWarning')}</Text>
        </Box>
      )}

      <Container maxW="5xl" py={8}>
        <Tabs colorScheme="brand" variant="enclosed" isFitted>
          <TabList mb={6}>
            <Tab fontWeight="600">{t('admin.tabPromotions')}</Tab>
            <Tab fontWeight="600">{t('admin.tabRealisations')}</Tab>
            <Tab fontWeight="600">{t('admin.tabJobs')}</Tab>
            <Tab fontWeight="600">{t('admin.tabApplications')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              <PromotionsPanel />
            </TabPanel>
            <TabPanel px={0}>
              <RealisationsPanel />
            </TabPanel>
            <TabPanel px={0}>
              <JobsPanel />
            </TabPanel>
            <TabPanel px={0}>
              <ApplicationsPanel />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Container>
    </Box>
  );
};

export default AdminDashboard;
